import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AiNarrativeFailurePayload,
  AiNarrativeIdPayload,
  AiNarrativePreview,
  AiNarrativeState,
  GenerateDraftRequestPayload,
  GenerateDraftSuccessPayload,
  RephraseRequestPayload,
  RephraseSuccessPayload,
} from './types';

const initialState: AiNarrativeState = {};

const idlePreview: AiNarrativePreview = { status: 'idle', mode: null };

const aiNarrativeSlice = createSlice({
  name: 'aiNarrative',
  initialState,
  reducers: {
    generateDraftRequest: (
      state,
      action: PayloadAction<GenerateDraftRequestPayload>,
    ) => {
      state[action.payload.narrativeId] = { status: 'loading', mode: 'draft' };
    },
    generateDraftSuccess: (
      state,
      action: PayloadAction<GenerateDraftSuccessPayload>,
    ) => {
      const { narrativeId, draftHtml, references } = action.payload;
      state[narrativeId] = {
        status: 'ready',
        mode: 'draft',
        draftHtml,
        references,
      };
    },
    generateDraftFailure: (
      state,
      action: PayloadAction<AiNarrativeFailurePayload>,
    ) => {
      const { narrativeId, error } = action.payload;
      state[narrativeId] = { status: 'error', mode: 'draft', error };
    },
    rephraseRequest: (state, action: PayloadAction<RephraseRequestPayload>) => {
      const {
        narrativeId,
        selectedText,
        action: rephraseAction,
      } = action.payload;
      state[narrativeId] = {
        status: 'loading',
        mode: 'rephrase',
        rephrase: {
          originalText: selectedText,
          proposedText: '',
          action: rephraseAction,
        },
      };
    },
    rephraseSuccess: (state, action: PayloadAction<RephraseSuccessPayload>) => {
      const { narrativeId, proposedText } = action.payload;
      const rephrase = state[narrativeId]?.rephrase;
      if (!rephrase) {
        return;
      }
      state[narrativeId] = {
        status: 'ready',
        mode: 'rephrase',
        rephrase: { ...rephrase, proposedText },
      };
    },
    rephraseFailure: (
      state,
      action: PayloadAction<AiNarrativeFailurePayload>,
    ) => {
      const { narrativeId, error } = action.payload;
      state[narrativeId] = { status: 'error', mode: 'rephrase', error };
    },
    acceptPreview: (state, action: PayloadAction<AiNarrativeIdPayload>) => {
      state[action.payload.narrativeId] = idlePreview;
    },
    rejectPreview: (state, action: PayloadAction<AiNarrativeIdPayload>) => {
      state[action.payload.narrativeId] = idlePreview;
    },
    clearPreview: (state, action: PayloadAction<AiNarrativeIdPayload>) => {
      delete state[action.payload.narrativeId];
    },
  },
});

export const {
  generateDraftRequest,
  generateDraftSuccess,
  generateDraftFailure,
  rephraseRequest,
  rephraseSuccess,
  rephraseFailure,
  acceptPreview,
  rejectPreview,
  clearPreview,
} = aiNarrativeSlice.actions;

export const selectAiPreview =
  (narrativeId: number) =>
  (state: { aiNarrative: AiNarrativeState }): AiNarrativePreview | undefined =>
    state.aiNarrative[narrativeId];

export const aiNarrativeReducer = aiNarrativeSlice.reducer;

export default aiNarrativeSlice;
